"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function HeaderContent() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <div className={`w-full transition-all duration-300 ${isScrolled ? "bg-background/80 backdrop-blur-[12px] border-b border-border/20 shadow-sm" : "bg-background border-b border-border/40"}`}>
      <header className="container mx-auto px-4 h-20 flex items-center justify-between">
        <div className="flex items-center flex-shrink-0">
          <Link to="/">
            <WvcLogo className="h-12 w-auto" />
          </Link>
        </div>

        <div className="hidden lg:flex flex-1 justify-center px-8">
          {loading ? <div className="flex gap-4">
              <div className="h-4 w-20 bg-muted animate-pulse rounded" />
              <div className="h-4 w-20 bg-muted animate-pulse rounded" />
              <div className="h-4 w-20 bg-muted animate-pulse rounded" />
            </div> : <NavigationMenu viewport={false}>
              <NavigationMenuList className="gap-2">
                {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
                    <NavigationMenuLink asChild>
                      <Link to={item.href || "#"} className="group relative inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-foreground transition-colors hover:text-ring focus:text-ring outline-none">
                        <span className="relative z-10 tracking-wider font-default">
                          {item.label}
                        </span>
                        <span className="absolute bottom-2 left-4 right-4 h-[1px] bg-ring origin-left scale-x-0 transition-transform duration-300 ease-out group-hover:scale-x-100" />
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>)}
              </NavigationMenuList>
            </NavigationMenu>}
        </div>

        <div className="lg:hidden flex items-center z-50">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:text-ring hover:bg-transparent" aria-label="Toggle mobile menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </header>

      {isMobileMenuOpen && <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-md border-b border-border/20 shadow-lg lg:hidden overflow-hidden origin-top animate-in slide-in-from-top-2 fade-in duration-200 z-40">
          <nav className="container mx-auto px-4 py-6 flex flex-col max-h-[calc(100vh-5rem)] overflow-y-auto">
            {loading ? <div className="flex flex-col gap-4 py-4">
                <div className="h-6 w-32 bg-muted animate-pulse rounded" />
                <div className="h-6 w-32 bg-muted animate-pulse rounded" />
              </div> : <div className="flex flex-col">
                {menuItems.map((item, i) => <div key={item.id} data-index={i}>
                    <Link to={item.href || "#"} className="block py-4 text-lg font-medium text-foreground border-b border-border/50 hover:text-ring transition-colors font-default" onClick={() => setIsMobileMenuOpen(false)}>
                      {item.label}
                    </Link>
                  </div>)}
              </div>}
          </nav>
        </div>}
    </div>;
}
export default function Header() {
  return <section data-section-id="233729" id="header" className="sticky top-0 z-50 w-full bg-background text-foreground">
      <MenuProvider menu_id="250" className="w-full">
        <HeaderContent />
      </MenuProvider>
    </section>;
}